import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface WorkerOption {
  id: number;
  name: string;
}

/** Above this many names the picker gets a search field. */
export const WORKER_SEARCH_FROM = 8;

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * "+ medewerker": opens a list of the company's workers that are not present yet and
 * marks the picked one present. Used by the administratie list and the installer
 * app, so both pick workers the same way.
 *
 * The dialog is portalled to <body> (the pattern of TaskDetailPopup / PhotoLightbox):
 * the cards (`overflow-hidden`) and the desktop table (`overflow-x-auto`) it is
 * opened from would otherwise clip it. A bottom sheet up to `lg` — the whole range
 * where the list is cards — and a centred dialog from `lg`. Focus moves into it,
 * Tab stays inside, and every way out (Escape, ×, backdrop, a pick) clears the
 * search and gives focus back to the "+ medewerker" button.
 */
export function WorkerAddPicker({
  workers,
  presentIds,
  onPick,
  disabled,
  isBusy,
  alert,
}: {
  workers: WorkerOption[];
  presentIds: Set<number>;
  onPick: (workerId: number) => void;
  disabled?: boolean;
  /** A write for that worker is running: it cannot be picked again yet. */
  isBusy?: (workerId: number) => boolean;
  /** Nobody is present yet and someone should be: the button turns red. */
  alert?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const available = useMemo(() => workers.filter((w) => !presentIds.has(w.id)), [workers, presentIds]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? available.filter((w) => w.name.toLowerCase().includes(q)) : available;
  }, [available, query]);

  const close = () => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    // First focus: the search field, else the first name, else the close button.
    const dialog = dialogRef.current;
    const first = dialog?.querySelector<HTMLElement>('input[type="search"]')
      ?? dialog?.querySelector<HTMLElement>('ul button:not([disabled])')
      ?? dialog?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // close only reads refs and setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Keep Tab inside the dialog. */
  const trapTab = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (items.length === 0) return;
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (e.shiftKey && document.activeElement === firstItem) {
      e.preventDefault();
      lastItem.focus();
    } else if (!e.shiftKey && document.activeElement === lastItem) {
      e.preventDefault();
      firstItem.focus();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`min-h-[36px] px-3 rounded-full border border-dashed text-sm font-semibold disabled:opacity-50 ${
          alert ? 'border-red-600 text-red-600 hover:bg-red-50' : 'border-td-blue text-td-blue hover:bg-td-blue/10'
        }`}
      >
        + medewerker
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60] bg-black/30" onClick={close} aria-hidden="true" data-testid="worker-picker-backdrop" />
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onKeyDown={trapTab}
            className="fixed z-[61] inset-x-0 bottom-0 max-h-[80vh] rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] shadow-xl flex flex-col lg:inset-x-auto lg:bottom-auto lg:top-24 lg:left-1/2 lg:-translate-x-1/2 lg:w-80 lg:max-h-[70vh] lg:rounded-xl"
          >
            <div className="flex items-center justify-between gap-2">
              <h3 id={titleId} className="text-sm font-bold text-td-navy">Kies medewerker</h3>
              <button type="button" onClick={close} aria-label="Sluiten" className="w-9 h-9 text-xl leading-none text-td-muted hover:text-td-navy">×</button>
            </div>
            {available.length > WORKER_SEARCH_FROM && (
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek een medewerker..."
                aria-label="Zoek een medewerker"
                className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            )}
            <ul className="mt-2 min-h-0 overflow-y-auto divide-y divide-gray-100" aria-label="Medewerkers">
              {shown.map((w) => (
                <li key={w.id}>
                  <button
                    type="button"
                    disabled={isBusy?.(w.id)}
                    onClick={() => {
                      onPick(w.id);
                      close();
                    }}
                    className="w-full min-h-[44px] text-left px-2 text-sm text-td-navy hover:bg-gray-50 focus:bg-gray-50 disabled:opacity-50"
                  >
                    {w.name}
                  </button>
                </li>
              ))}
            </ul>
            {available.length === 0 && <p className="mt-2 text-sm text-td-muted">Iedereen is al aanwezig.</p>}
            {available.length > 0 && shown.length === 0 && <p className="mt-2 text-sm text-td-muted">Geen medewerker gevonden.</p>}
            <p className="mt-2 text-xs text-td-muted">Staat een medewerker er niet bij? Vraag het kantoor om hem toe te voegen.</p>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
